/**
 * 원본 평문(`source_text`)을 **글자 조각과 수식 조각으로 나눈다.**
 *
 * 변형 검수는 원본과 변형을 눈으로 견주는 자리다. 그런데 변형 결과는 KaTeX 로 그려지고
 * 원본만 `\(A=\left\{x \mid x=2^{a} \times 3^{b}, a, b\right.\)` 같은 날 LaTeX 로 남아 있었다 —
 * 같은 문장이 위아래에서 다른 얼굴이라, 어디가 바뀐 것인지 글자를 세어 가며 읽어야 했다.
 *
 * 구분자는 `\(...\)`(인라인)와 `\[...\]`(디스플레이) **둘만** 본다. `$...$` 는 보지 않는다 —
 * 본문 평문에는 달러가 금액으로도 오고, 짝이 맞는지 알 길이 없어 한 번의 오탐이 그 뒤 문장을
 * 통째로 수식으로 삼켜 버린다. 정답처럼 **한 조각짜리** 값은 규칙이 다르다(→ OcrHub/answerLatex.js).
 *
 * 나누기만 하고 고치지 않는다. 원본은 대조 대상이라, 보기 번호 `(1)`을 `①`로 바꾸는 식의
 * 손질을 하면 「원본이 이렇게 생겼다」가 더 이상 참이 아니게 된다.
 *
 * @param {string} raw 원본 평문
 * @returns {Array<{math: boolean, value: string, raw: string}>}
 *   math=true 면 `value` 는 구분자를 벗긴 LaTeX, `raw` 는 구분자까지 붙은 원문이다.
 *   렌더가 실패하면 `raw` 를 그대로 보여 준다 — 못 그린 자리가 비면 지금보다 나쁘다.
 */
export function splitSourceLatex(raw) {
  const s = String(raw ?? '');
  if (s === '') return [];

  // [\s\S] 로 쓴다 — dotAll 플래그 없이 줄바꿈을 넘는 \[...\] 까지 잡는다.
  // 게으른 수량자라 가장 가까운 닫는 구분자에서 끊긴다(수식 하나가 다음 수식을 삼키지 않게).
  const re = /\\\(([\s\S]*?)\\\)|\\\[([\s\S]*?)\\\]/g;
  const out = [];
  let last = 0;
  let m = re.exec(s);

  while (m !== null) {
    if (m.index > last) {
      const text = s.slice(last, m.index);
      out.push({ math: false, value: text, raw: text });
    }
    const body = (m[1] !== undefined ? m[1] : m[2]).trim();
    // 빈 수식은 수식으로 세지 않는다 — KaTeX 가 빈 문자열에 아무것도 그리지 않아
    // 그 자리에 있던 표기가 소리 없이 사라진다
    out.push(body === ''
      ? { math: false, value: m[0], raw: m[0] }
      : { math: true, value: body, raw: m[0] });
    last = m.index + m[0].length;
    m = re.exec(s);
  }

  // 짝이 맞지 않아 끝까지 닫히지 않은 `\(` 는 여기로 떨어져 글자로 남는다
  if (last < s.length) {
    const tail = s.slice(last);
    out.push({ math: false, value: tail, raw: tail });
  }
  return out;
}

export default splitSourceLatex;
